import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alankar_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const applyToken = useCallback((t) => {
    if (t) {
      axios.defaults.headers.common.Authorization = `Bearer ${t}`;
      localStorage.setItem(TOKEN_KEY, t);
    } else {
      delete axios.defaults.headers.common.Authorization;
      localStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  // On first load, if a token is present, try to fetch /me
  useEffect(() => {
    let cancelled = false;
    const fetchMe = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      applyToken(token);
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(res.data);
        // Fetch admin flag alongside; failures leave isAdmin=false.
        try {
          const a = await axios.get(`${API}/auth/is-admin`);
          if (!cancelled) setIsAdmin(!!(a.data && a.data.isAdmin));
        } catch (_) {
          if (!cancelled) setIsAdmin(false);
        }
      } catch (_) {
        if (!cancelled) {
          setUser(null);
          setIsAdmin(false);
          setToken(null);
          applyToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMe();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _refreshAdmin = useCallback(async () => {
    try {
      const a = await axios.get(`${API}/auth/is-admin`);
      setIsAdmin(!!(a.data && a.data.isAdmin));
    } catch (_) {
      setIsAdmin(false);
    }
  }, []);

  const login = useCallback(
    async (email, password) => {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const { token: t, user: u } = res.data;
      applyToken(t);
      setToken(t);
      setUser(u);
      await _refreshAdmin();
      return u;
    },
    [applyToken, _refreshAdmin]
  );

  const register = useCallback(
    async ({ name, email, password, phone }) => {
      const res = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        phone: phone || undefined,
      });
      const { token: t, user: u } = res.data;
      applyToken(t);
      setToken(t);
      setUser(u);
      await _refreshAdmin();
      return u;
    },
    [applyToken, _refreshAdmin]
  );

  const logout = useCallback(() => {
    applyToken(null);
    setToken(null);
    setUser(null);
    setIsAdmin(false);
  }, [applyToken]);

  const value = { user, isAdmin, loading, login, register, logout, isAuthenticated: !!user };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
